import type { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';

import { useToast } from '@admin-platform/ui/components';

import { EmailNotificationScope } from '../../graphql/enums.graphql';

import { NOTIFICATION_CENTER_PREFERENCES_QUERY } from './notificationCenterOps';
import EMAIL_SCOPE from './notificationCenterEmailScope.query.graphql';
import type {
  NotificationCenterEmailScopeQuery,
  NotificationCenterEmailScopeQueryVariables,
} from './notificationCenterEmailScope.query.graphql';
import SET_EMAIL_SCOPE from './setNotificationCenterEmailScope.mutation.graphql';
import type {
  SetNotificationCenterEmailScopeMutation,
  SetNotificationCenterEmailScopeMutationVariables,
} from './setNotificationCenterEmailScope.mutation.graphql';

export const NOTIFICATION_CENTER_EMAIL_SCOPE_QUERY = EMAIL_SCOPE as TypedDocumentNode<
  NotificationCenterEmailScopeQuery,
  NotificationCenterEmailScopeQueryVariables
>;
export const SET_NOTIFICATION_CENTER_EMAIL_SCOPE_MUTATION = SET_EMAIL_SCOPE as TypedDocumentNode<
  SetNotificationCenterEmailScopeMutation,
  SetNotificationCenterEmailScopeMutationVariables
>;

export const EMAIL_SCOPE_TITLE = 'New email notifications';
export const EMAIL_SCOPE_HINT = 'Which incoming email puts a notification in your bell.';

/** In the order they are offered: from nothing to everything. */
export const EMAIL_SCOPE_OPTIONS: { value: EmailNotificationScope; label: string }[] = [
  { value: EmailNotificationScope.None, label: 'Off' },
  { value: EmailNotificationScope.Clients, label: 'Clients only' },
  { value: EmailNotificationScope.Prospects, label: 'Prospects only' },
  { value: EmailNotificationScope.ClientsAndProspects, label: 'Clients and prospects' },
  { value: EmailNotificationScope.All, label: 'Any email' },
];

/** Mirrors the API's default for someone who has not chosen yet. */
const DEFAULT_SCOPE = EmailNotificationScope.ClientsAndProspects;

export function isEmailNotificationScope(value: unknown): value is EmailNotificationScope {
  return EMAIL_SCOPE_OPTIONS.some((option) => option.value === value);
}

/** The three senders a person can put in the bell; "others" is everyone else. */
export interface EmailScopeSenders {
  clients: boolean;
  prospects: boolean;
  others: boolean;
}

export function sendersOfScope(scope: EmailNotificationScope): EmailScopeSenders {
  switch (scope) {
    case EmailNotificationScope.Clients:
      return { clients: true, prospects: false, others: false };
    case EmailNotificationScope.Prospects:
      return { clients: false, prospects: true, others: false };
    case EmailNotificationScope.ClientsAndProspects:
      return { clients: true, prospects: true, others: false };
    case EmailNotificationScope.All:
      return { clients: true, prospects: true, others: true };
    default:
      return { clients: false, prospects: false, others: false };
  }
}

/** "Others" is only reachable as Any email, which already includes clients and prospects. */
export function scopeOfSenders({
  clients,
  prospects,
  others,
}: EmailScopeSenders): EmailNotificationScope {
  if (others) return EmailNotificationScope.All;
  if (clients && prospects) return EmailNotificationScope.ClientsAndProspects;
  if (clients) return EmailNotificationScope.Clients;
  if (prospects) return EmailNotificationScope.Prospects;
  return EmailNotificationScope.None;
}

/**
 * The caller's choice of which incoming email reaches their bell. Saves on
 * pick (one value, nothing to review), optimistically; a failure rolls back
 * and says so.
 */
export function useEmailNotificationScope({ skip = false }: { skip?: boolean } = {}): {
  scope: EmailNotificationScope;
  loaded: boolean;
  failed: boolean;
  saving: boolean;
  setScope: (scope: EmailNotificationScope) => void;
} {
  const toast = useToast();
  const { data, error } = useQuery(NOTIFICATION_CENTER_EMAIL_SCOPE_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip,
  });
  const [save, saveState] = useMutation(SET_NOTIFICATION_CENTER_EMAIL_SCOPE_MUTATION);

  const setScope = (scope: EmailNotificationScope) => {
    void save({
      variables: { scope },
      optimisticResponse: { setNotificationCenterEmailScope: scope },
      update(cache, result) {
        const next = result.data?.setNotificationCenterEmailScope;
        if (next) {
          cache.writeQuery({
            query: NOTIFICATION_CENTER_EMAIL_SCOPE_QUERY,
            data: { notificationCenterEmailScope: next },
          });
        }
      },
      // "Off" is the new-email kind switched off; keep the per-kind list in step.
      refetchQueries: [NOTIFICATION_CENTER_PREFERENCES_QUERY],
    }).catch(() => toast('Could not save that change. Try again.'));
  };

  return {
    scope: data?.notificationCenterEmailScope ?? DEFAULT_SCOPE,
    loaded: !!data,
    failed: !!error && !data,
    saving: saveState.loading,
    setScope,
  };
}
